import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { cognitoConfirmSignUp, cognitoResendConfirmationCode } from './cognito';

export interface VerificationModalProps {
  email: string;
  loginFunc: () => Promise<any>;
  clientId: string;
  region: string;
}

export default function VerificationModal(props: VerificationModalProps) {
  const [open, setOpen] = useState(true);
  const [verificationDone, setVerificationDone] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const title = verificationDone ? 'Signup Successful' : 'Confirm E-Mail';
  const text = verificationDone ? 'You can now login' : 'Please enter the confirmation code';

  const confirmEmail = async (verificationCode: string) => {
    const success = await cognitoConfirmSignUp(
      props.region,
      props.clientId,
      props.email,
      verificationCode,
    );
    if (!success) {
      return alert('confirmation wrong!');
    }
    setVerificationDone(true);
  };

  const resendVerificationCode = async () => {
    setResending(true);
    setResendMessage('');
    const success = await cognitoResendConfirmationCode(props.region, props.clientId, props.email);
    if (success) {
      setResendMessage('Verification code sent! Check your email.');
    } else {
      setResendMessage('Failed to resend verification code. Please try again.');
    }
    setResending(false);
  };

  const loginAndClose = async () => {
    setLoggingIn(true);
    await props.loginFunc();
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="relative z-10">
      {/* DialogBackdrop */}
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true"></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left
            shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
          >
            <div>
              {verificationDone && (
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon aria-hidden="true" className="size-6 text-green-600" />
                </div>
              )}
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{text}</p>
                </div>
              </div>
            </div>
            {!verificationDone && (
              <div className="mt-4">
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base
                      text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300
                       placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2
                       focus:outline-indigo-600 sm:text-sm/6"
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value.length === 6) {
                      confirmEmail(value);
                    }
                  }}
                />
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={resendVerificationCode}
                    disabled={resending}
                    className="text-sm text-indigo-600 hover:text-indigo-500 disabled:text-gray-400
                    disabled:cursor-not-allowed"
                  >
                    {resending ? 'Sending...' : "Didn't receive the code? Resend"}
                  </button>
                  {resendMessage && (
                    <p
                      className={`mt-1 text-xs ${
                        resendMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {resendMessage}
                    </p>
                  )}
                </div>
              </div>
            )}
            {verificationDone && (
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={() => {
                    loginAndClose();
                  }}
                  disabled={loggingIn}
                  className="cursor-pointer inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2
                  text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2
                  focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-300
                  disabled:cursor-not-allowed"
                >
                  {loggingIn ? '...' : 'Login'}
                </button>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
